import { useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FilterPanel, { type SortKey } from '@/components/feature/FilterPanel';
import MovieGrid from '@/components/feature/MovieGrid';
import Pagination from '@/components/feature/Pagination';
import { categoryMeta, getCategoryMeta } from '@/lib/categoryMeta';
import { filterMovies } from '@/lib/movieData';
import useDocumentMeta from '@/hooks/useDocumentMeta';
import { yearRangeOptions } from '@/types/movie';

const PAGE_SIZE = 12;

function parseList(value: string | null): string[] {
  return value ? value.split(',').filter(Boolean) : [];
}

export default function CategoryPage() {
  const { type = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const meta = getCategoryMeta(type);

  useDocumentMeta(
    meta ? `${meta.title}频道 - 久久热影视分类` : '影视分类 - 久久热',
    meta ? `${meta.title} —— ${meta.description}` : '久久热影视分类页，按类型、地区、年份筛选影视内容。'
  );

  const genreSelected = parseList(searchParams.get('genre'));
  const regionSelected = parseList(searchParams.get('region'));
  const yearSelected = parseList(searchParams.get('year'));
  const sort = (searchParams.get('sort') as SortKey) || 'hot';
  const page = Math.max(1, Number(searchParams.get('page')) || 1);

  const update = (patch: Record<string, string | null>, resetPage = true) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === '') next.delete(k);
      else next.set(k, v);
    });
    if (resetPage) next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const toggle = (key: 'genre' | 'region' | 'year', value: string) => {
    const current = parseList(searchParams.get(key));
    const nextList = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    update({ [key]: nextList.join(',') });
  };

  const reset = () => {
    const next = new URLSearchParams();
    if (sort !== 'hot') next.set('sort', sort);
    setSearchParams(next, { replace: true });
  };

  const years = useMemo(
    () => yearSelected
      .map((label) => yearRangeOptions.find((y) => y.label === label))
      .filter((y): y is { label: string; min: number; max: number } => Boolean(y)),
    [yearSelected]
  );

  const results = useMemo(() => {
    if (!meta) return [];
    return filterMovies({ type: meta.type, genres: genreSelected, regions: regionSelected, years, sort });
  }, [meta, genreSelected, regionSelected, years, sort]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = results.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  if (!meta) {
    return (
      <div className="w-full px-4 md:px-8 py-20 text-center">
        <h1 className="font-display text-3xl font-bold text-foreground-950">未找到该分类</h1>
        <p className="mt-3 text-foreground-500">你访问的影视分类不存在，试试下面的入口。</p>
        <div className="mt-6 flex justify-center gap-3 flex-wrap">
          <Link to="/" className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-home-4-line text-lg" />返回首页
          </Link>
          <Link to="/category/movie" className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-300 text-foreground-700 text-sm font-semibold hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            去电影频道
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <section className="w-full px-4 md:px-8 pt-8 md:pt-10 pb-6 bg-background-100 border-b border-background-200">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: meta.title }]} />
        <h1 className="mt-4 font-display text-3xl md:text-4xl font-bold text-foreground-950">
          {meta.title}频道
        </h1>
        <p className="mt-2 text-sm md:text-base text-foreground-500 max-w-3xl leading-relaxed">
          {meta.title} — {meta.description}
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          {(['movie', 'tv', 'variety'] as const).map((t) => (
            <Link
              key={t}
              to={`/category/${t}`}
              className={`h-9 px-4 inline-flex items-center rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${t === meta.type ? 'bg-primary-500 text-background-50' : 'bg-background-50 border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600'}`}
            >
              {categoryMeta[t].title}
            </Link>
          ))}
        </div>
      </section>

      {/* Filters */}
      <section className="w-full px-4 md:px-8 py-6">
        <FilterPanel
          genreOptions={meta.genres}
          genreSelected={genreSelected}
          onToggleGenre={(g) => toggle('genre', g)}
          regionSelected={regionSelected}
          onToggleRegion={(r) => toggle('region', r)}
          yearSelected={yearSelected}
          onToggleYear={(y) => toggle('year', y)}
          sort={sort}
          onSortChange={(s) => update({ sort: s === 'hot' ? null : s })}
          onReset={reset}
        />
      </section>

      {/* Results */}
      <section className="w-full px-4 md:px-8 pb-14 md:pb-20">
        <div className="flex items-center justify-between mb-5">
          <p className="text-sm text-foreground-500">
            共找到 <span className="font-semibold text-primary-600">{results.length}</span> 部{meta.title}
            {totalPages > 1 && <span className="ml-2 text-foreground-400">· 第 {safePage} / {totalPages} 页</span>}
          </p>
        </div>

        {pageItems.length > 0 ? (
          <>
            <MovieGrid movies={pageItems} columns={4} />
            <Pagination page={safePage} totalPages={totalPages} onChange={(p) => update({ page: String(p) }, false)} />
          </>
        ) : (
          <div className="py-16 text-center rounded-lg border border-dashed border-background-300 bg-background-100">
            <span className="inline-flex w-14 h-14 rounded-full bg-background-200 items-center justify-center">
              <i className="ri-search-eye-line text-2xl text-foreground-400" />
            </span>
            <h3 className="mt-4 font-display text-lg font-bold text-foreground-900">没有匹配的内容</h3>
            <p className="mt-1.5 text-sm text-foreground-500">试试减少筛选条件，或重置后重新浏览。</p>
            <button type="button" onClick={reset} className="mt-5 inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-refresh-line" />重置筛选
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

